import React from 'react'
import CartProduct from '../../types/CartTypes'
import useCartStore from '../../store/cart'
import { TrashIcon } from '@heroicons/react/20/solid'
import { priceForPlan } from '@/lib/priceConversions'

interface CartItemCardProps {
  product: CartProduct
}

const CartItem: React.FC<CartItemCardProps> = ({ product }) => {
  const { setQuantity, removeItemFromCart } = useCartStore()

  const excludedIds = ['VCU-MONTH-TECHNET', 'VCU-MONTH']

  const formatPrice = () => {
    if (product.id === 'CTIU-MONTH') {
      const base = 129
      const extra = 45
      const extras = product.quantity - 1
      const total = base + extras * extra

      return (
        <>
          <span className="block">
            ${base} <span className="text-gray-500">(1st user)</span>
          </span>
          {extras > 0 && (
            <span className="block">
              + {extras} x ${extra}{' '}
              <span className="text-gray-500">
                (additional {extras === 1 ? 'user' : 'users'})
              </span>
            </span>
          )}
          <span className="block font-semibold text-gray-800 mt-1">
            ${total.toFixed(2)} / month
          </span>
        </>
      )
    }

    if (!excludedIds.includes(product.id)) {
      return `${product.quantity} x ${priceForPlan(product)}`
    }

    return priceForPlan(product)
  }

  const onRemoveItem = (productId: string) => {
    removeItemFromCart(productId)
  }

  const optionsForProduct = (product: CartProduct) => {
    if (product.product_type === 'olt') {
      return null
    }

    const maxQty = ['VCU-MONTH', 'VCU-MONTH-TECHNET'].includes(product.id)
      ? 1
      : 10

    return (
      <div className="ml-4 flex flex-row gap-4 items-center">
        {product.id === 'CTIU-MONTH' && (
          <label className="mb-1 text-xs font-semibold text-gray-700">
            Number of users
          </label>
        )}
        <label htmlFor="quantity" className="sr-only">
          Quantity
        </label>
        <select
          id="quantity"
          name="quantity"
          className="rounded-md border border-gray-300 text-left text-base font-medium text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
          onChange={({ target: { value } }) =>
            setQuantity(product.id, parseInt(value))
          }
          value={product.quantity}
        >
          {Array.from({ length: maxQty }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}
            </option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <div className="ml-6 flex flex-1 flex-col">
      <div className="flex">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-medium text-gray-700 hover:text-gray-800">
            {product.name} - {product.id}
          </h4>
          <p className="mt-1 text-sm text-gray-500">{product.description}</p>
        </div>

        <div className="ml-4 flow-root flex-shrink-0">
          <button
            type="button"
            className="-m-2.5 flex items-center justify-center bg-white p-2.5 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Remove</span>
            <TrashIcon
              aria-hidden="true"
              className="h-5 w-5"
              onClick={() => onRemoveItem(product.id)}
            />
          </button>
        </div>
      </div>

      <div className="flex flex-1 items-end justify-between pt-2">
        <p className="mt-1 text-sm font-medium text-gray-900">
          {formatPrice()}
        </p>
        {!excludedIds.includes(product.id) && optionsForProduct(product)}
      </div>
    </div>
  )
}

export default CartItem
